import {
  SOURCE_FIELDS,
  DATA_SOURCE_OPTIONS,
  type DataSource,
} from '@/features/reports/hooks/useReportBuilder';
import { reportBaseVariables, type TemplateVariable } from '../hooks/useDocumentTemplates';

/**
 * Report data sources double as the "Report Categories" a report template can
 * target. Re-exported from the reports module so the template editor stays in
 * sync with the report builder's catalog.
 */
export const REPORT_DATA_SOURCES = DATA_SOURCE_OPTIONS;

export type ReportDataSource = DataSource;

/**
 * Builds the placeholder/variable list for a report template targeting the given
 * data source. Combines the fixed report-level fields (report.*, data, columns)
 * with the per-source field catalog. Row-level fields are surfaced under the
 * `row.` prefix so templates can reference them inside a `{{#each data}}` loop.
 */
export function reportVariablesForSource(source: ReportDataSource | null): TemplateVariable[] {
  const base = reportBaseVariables();
  if (!source) return base;

  const fields = SOURCE_FIELDS[source] ?? [];
  const rowFields: TemplateVariable[] = fields.map((f) => ({
    // Inside {{#each data}} the field key is `<source>_<name>` (report-builder
    // flattens `source.name` → `source_name`), exposed here as row.<key>.
    key: `row.${f.source}_${f.name}`,
    label: `${f.label} (row)`,
    type: mapFieldType(f.type),
    source: `${source} rows`,
  }));

  return [...base, ...rowFields];
}

/**
 * Static fallback list of a data source's columns, derived from the field
 * catalog. Ids match the flattened row keys (`<source>_<field>`) the report
 * builder produces. Used before the report has been run to discover its real
 * columns.
 */
export function staticColumnsForSource(
  source: ReportDataSource | null,
): { id: string; label: string }[] {
  if (!source) return [];
  const fields = SOURCE_FIELDS[source] ?? [];
  return fields.map((f) => ({ id: `${f.source}_${f.name}`, label: f.label }));
}

function mapFieldType(type: 'text' | 'number' | 'date' | 'boolean'): string {
  switch (type) {
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}
